import React from 'react';
import { css } from '@emotion/react';

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 96px;
  padding: 0 48px;
  background: var(--grn-color-background);
  overflow: hidden;
`;

const logoContainerStyles = css`
  width: 280px;
  display: flex;
  align-items: center;
`;

const navStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const navItemStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--grn-color-content);
  }
`;

const navItemActiveStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
  cursor: pointer;
`;

const accountStyles = css`
  width: 280px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
`;

const avatarStyles = css`
  width: 40px;
  height: 40px;
  border-radius: 100px;
  border: 1px solid #c9c9c9;
  object-fit: cover;
`;

const chevronStyles = css`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const logoClickStyles = css`
  cursor: pointer;
`;

type HeaderProps = {
  activeNav?: string;
  logoVariant?: 'slowdesk' | 'flodesk';
  onLogoClick?: () => void;
};

const NAV_ITEMS = ['Emails', 'Forms', 'Workflows', 'Checkouts', 'Audience', 'Analytics'];

export default function Header({
  activeNav = 'Audience',
  logoVariant = 'slowdesk',
  onLogoClick,
}: HeaderProps) {
  return (
    <header css={headerStyles}>
      <div
        css={[logoContainerStyles, onLogoClick && logoClickStyles]}
        role="button"
        tabIndex={0}
        onClick={onLogoClick}
        onKeyDown={e => e.key === 'Enter' && onLogoClick?.()}
      >
        {logoVariant === 'flodesk' ? (
          <svg
            width="57"
            height="12"
            viewBox="0 0 57 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            css={css`
              height: 13px;
              width: auto;
            `}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.3188 3.02031C16.9384 3.02031 18.8421 4.93639 18.8421 7.51821C18.8421 10.1001 16.9384 12 14.3188 12C11.6667 12 9.81182 10.1001 9.81182 7.51821C9.81182 4.93639 11.6667 3.02031 14.3188 3.02031ZM14.3188 4.74153C12.7568 4.74153 11.748 5.92696 11.748 7.502C11.748 9.12584 12.7568 10.295 14.3188 10.295C15.8971 10.295 16.8733 9.10952 16.8733 7.502C16.8733 5.94317 15.8808 4.74153 14.3188 4.74153Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M35.1292 3.02031C37.944 3.02031 39.4735 5.08255 39.4735 7.59945C39.4735 7.7131 39.4735 7.92421 39.4573 8.08661H32.6235C32.6886 9.4993 33.8601 10.3599 35.2268 10.3599C36.1542 10.3599 36.9353 9.9377 37.4234 9.12586L39.3108 9.46685C38.7739 11.0906 36.984 11.9837 35.1455 11.9837C32.5096 11.9837 30.6873 10.1326 30.6873 7.51821C30.6873 4.96891 32.5259 3.02033 35.1292 3.02031ZM35.1292 4.61166C33.795 4.61167 32.8512 5.43991 32.656 6.60902H37.4559C37.2769 5.35866 36.4471 4.61166 35.1292 4.61166Z"
              fill="#111110"
            />
            <path
              d="M44.0942 2.9878C45.9002 2.9878 47.1695 3.97828 47.2996 5.55337L45.461 5.6184C45.3146 4.98506 44.8101 4.53042 43.9966 4.53042C43.1994 4.53042 42.7275 4.9201 42.7275 5.47213C42.7275 5.92685 43.0041 6.13794 43.6875 6.3653L45.3308 6.9336C46.6975 7.40454 47.4949 7.9566 47.4949 9.25564C47.4949 11.0256 46.0467 11.9836 44.0942 11.9836C42.1254 11.9836 40.7749 11.0256 40.6448 9.33679L42.581 9.25564C42.6299 9.88892 43.1505 10.4735 44.1105 10.4735C45.0054 10.4735 45.4934 10.0513 45.4934 9.46676C45.4934 8.89844 45.1193 8.70355 44.2081 8.39508L43.0041 8.00528C41.6211 7.56688 40.775 6.90115 40.775 5.63461C40.775 4.0433 42.1091 2.9878 44.0942 2.9878Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M28.9135 11.7721H27.1074V11.155C27.1074 10.9276 27.1074 10.749 27.14 10.6029H27.0749C26.489 11.4635 25.4478 11.9669 24.2112 11.9669C21.7706 11.9669 20.0947 10.0833 20.0947 7.53396C20.0947 4.98458 21.7705 3.03599 24.1298 3.03596C25.3827 3.03596 26.4241 3.57189 26.9772 4.35136H27.0261C27.0098 3.45824 26.9935 3.23089 26.9935 2.22413V0.0481693H28.9135V11.7721ZM24.5691 4.77349C23.0072 4.7735 22.0309 6.00759 22.0309 7.51766C22.0309 9.06033 23.0234 10.2781 24.5691 10.2781C26.0986 10.2781 27.1074 9.07655 27.1074 7.50145C27.1074 5.95883 26.0986 4.77349 24.5691 4.77349Z"
              fill="#111110"
            />
            <path
              d="M4.03511 0C4.21413 0 4.39312 4.0185e-05 4.60462 0.0325143V1.70501C4.53955 1.68875 4.49066 1.6888 4.40935 1.6888C3.69348 1.68881 3.22165 1.93235 3.12406 2.6143C3.10776 2.76049 3.10776 2.85791 3.10776 2.98789V3.23142H4.60462V4.85519H3.12406V11.7726H1.20405V4.85519H0V3.23142H1.20405V2.93916C1.20405 1.10429 2.03387 1.08386e-05 4.03511 0Z"
              fill="#111110"
            />
            <path d="M8.03733 11.772H6.08481V0.0481693H8.03733V11.772Z" fill="#111110" />
            <path
              d="M51.1215 2.67868C51.1215 4.10764 51.089 5.00074 51.0727 6.26729L51.089 6.2836C51.691 5.63411 52.2442 5.14693 52.8624 4.51365L54.1153 3.23087H56.7675L52.2279 7.25782L56.9139 11.772H54.1642C53.0903 10.7003 52.0489 9.57995 51.1053 8.52449H51.089C51.1053 9.23892 51.1215 9.82351 51.1215 10.6353V11.772H49.2179V0.0481693H51.1215V2.67868Z"
              fill="#111110"
            />
          </svg>
        ) : (
          <svg
            width="63"
            height="11"
            viewBox="0 0 63 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            css={css`
              height: 13px;
              width: auto;
            `}
          >
            <path
              d="M14.4609 2.5625C16.7149 2.5625 18.3525 4.21443 18.3525 6.44043C18.3523 8.66622 16.7148 10.3037 14.4609 10.3037C12.1791 10.3037 10.5832 8.66619 10.583 6.44043C10.583 4.21445 12.179 2.56253 14.4609 2.5625ZM2.96777 2.53418C4.52177 2.53418 5.61359 3.38809 5.72559 4.74609L4.14355 4.80176C4.01754 4.25578 3.58377 3.86328 2.88379 3.86328C2.19792 3.86335 1.79199 4.19983 1.79199 4.67578C1.79203 5.06772 2.03021 5.24933 2.61816 5.44531L4.03223 5.93555C5.20806 6.34151 5.89348 6.81769 5.89355 7.9375C5.89355 9.4635 4.64777 10.29 2.96777 10.29C1.27394 10.29 0.11205 9.46367 0 8.00781L1.66602 7.9375C1.70802 8.4835 2.15642 8.9873 2.98242 8.9873C3.75194 8.98717 4.17164 8.6238 4.17188 8.12012C4.17188 7.63017 3.85023 7.46126 3.06641 7.19531L2.03027 6.85938C0.840273 6.48138 0.112305 5.90743 0.112305 4.81543C0.112451 3.44364 1.26001 2.53426 2.96777 2.53418ZM43.458 2.5625C45.8797 2.56263 47.1952 4.33996 47.1953 6.50977C47.1953 6.60769 47.1956 6.78973 47.1816 6.92969H41.3018C41.3578 8.14769 42.366 8.88965 43.542 8.88965C44.3398 8.88957 45.0117 8.52599 45.4316 7.82617L47.0557 8.12012C46.5936 9.52004 45.0536 10.29 43.4717 10.29C41.2039 10.29 39.6359 8.69419 39.6357 6.44043C39.6357 4.24243 41.218 2.5625 43.458 2.5625ZM51.1748 2.53418C52.7288 2.53418 53.8206 3.38809 53.9326 4.74609L52.3506 4.80176C52.2246 4.25578 51.7908 3.86328 51.0908 3.86328C50.4049 3.86335 49.999 4.19983 49.999 4.67578C49.9991 5.06772 50.2372 5.24933 50.8252 5.44531L52.2393 5.93555C53.4151 6.34151 54.1005 6.81769 54.1006 7.9375C54.1006 9.4635 52.8548 10.29 51.1748 10.29C49.481 10.29 48.3191 9.46367 48.207 8.00781L49.873 7.9375C49.915 8.4835 50.3635 8.9873 51.1895 8.9873C51.959 8.98717 52.3787 8.6238 52.3789 8.12012C52.3789 7.63017 52.0573 7.46126 51.2734 7.19531L50.2373 6.85938C49.0473 6.48138 48.3193 5.90743 48.3193 4.81543C48.3195 3.44364 49.467 2.53426 51.1748 2.53418ZM38.1045 10.1084H36.5508V9.57617C36.5508 9.38017 36.5511 9.22561 36.5791 9.09961H36.5225C36.0185 9.84161 35.1226 10.2764 34.0586 10.2764C31.9588 10.2762 30.5166 8.65196 30.5166 6.4541C30.5166 4.2561 31.9593 2.57617 33.9893 2.57617C35.067 2.57629 35.9625 3.03815 36.4385 3.70996H36.4805C36.4665 2.94 36.4531 2.74396 36.4531 1.87598V0H38.1045V10.1084ZM9.06152 10.1084H7.38086V0H9.06152V10.1084ZM57.2266 2.26758C57.2266 3.49958 57.1986 4.2703 57.1846 5.3623L57.1982 5.37598C57.7162 4.81601 58.1926 4.39559 58.7246 3.84961L59.8018 2.74414H62.084L58.1777 6.21582L62.21 10.1084H59.8438C58.9197 9.1844 58.0239 8.21762 57.2119 7.30762H57.1982C57.2122 7.9236 57.2266 8.42797 57.2266 9.12793V10.1084H55.5879V0H57.2266V2.26758ZM21.6309 6.25781C21.8268 6.95778 21.9954 7.65778 22.1494 8.34375H22.1914C22.3454 7.65775 22.5133 6.97148 22.6953 6.27148L23.5908 2.74414H25.3135L26.2373 6.2998C26.4193 7.0277 26.5586 7.57383 26.7266 8.30176H26.7686C26.9366 7.57377 27.0774 7.02778 27.2734 6.2998L28.2246 2.74414H29.9189L27.5527 10.1074H25.873L24.4727 4.5498H24.4307C24.2627 5.2637 24.1374 5.82384 23.9414 6.55176L22.9746 10.1074H21.2949L18.915 2.74414H20.6787L21.6309 6.25781ZM14.4609 4.0459C13.1171 4.04593 12.2491 5.06792 12.249 6.42578C12.249 7.82576 13.117 8.83395 14.4609 8.83398C15.8189 8.83398 16.6592 7.81178 16.6592 6.42578C16.6591 5.08189 15.8049 4.0459 14.4609 4.0459ZM34.3672 4.07422C33.0232 4.07422 32.1826 5.13843 32.1826 6.44043C32.1828 7.77023 33.0373 8.82031 34.3672 8.82031C35.683 8.82013 36.5508 7.78366 36.5508 6.42578C36.5507 5.096 35.6829 4.0744 34.3672 4.07422ZM43.458 3.93359C42.31 3.93359 41.4981 4.64825 41.3301 5.65625H45.46C45.306 4.57833 44.5919 3.93369 43.458 3.93359Z"
              fill="#251B18"
            />
          </svg>
        )}
      </div>
      <nav css={navStyles}>
        {NAV_ITEMS.map(item => (
          <span key={item} css={item === activeNav ? navItemActiveStyles : navItemStyles}>
            {item}
          </span>
        ))}
      </nav>
      <div css={accountStyles}>
        <img
          css={avatarStyles}
          src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face"
          alt="Avatar"
        />
        <div css={chevronStyles}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1L5 5L9 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </header>
  );
}
